'use client';

import { standingFoodImages } from '@/lib/food-images';
import { Bodies, Body, Engine, Runner, World } from 'matter-js';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

type ViewportRange = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

function getViewportRange(width: number): ViewportRange {
    if (width <= 885) return 'sm';
    if (width < 1024) return 'md';
    if (width < 1537) return 'lg';
    if (width < 1921) return 'xl';
    return '2xl';
}

export function StandingOverlay() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [viewport, setViewport] = useState<ViewportRange>('lg');
    const [engine] = useState(() => Engine.create());
    const runnerRef = useRef<Runner | null>(null);
    const bodiesRef = useRef<Record<number, Matter.Body>>({});
    const [positions, setPositions] = useState<
        Record<number, { x: number; y: number }>
    >({});

    useEffect(() => {
        const update = () => setViewport(getViewportRange(window.innerWidth));
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        const world = engine.world;
        engine.gravity.y = 1;
        engine.gravity.x = 0;
        engine.gravity.scale = 0.01;

        const width = window.innerWidth;
        const height = window.innerHeight;
        const centerX = width / 2;
        const thickness = 100;

        // Boundaries
        if (Object.keys(bodiesRef.current).length === 0) {
            const boundaries = [
                Bodies.rectangle(width / 2, -height, width * 3, thickness, {
                    isStatic: true,
                }),
                Bodies.rectangle(
                    -thickness,
                    height / 2,
                    thickness,
                    height * 3,
                    {
                        isStatic: true,
                    },
                ),
                Bodies.rectangle(
                    width + thickness,
                    height / 2,
                    thickness,
                    height * 3,
                    {
                        isStatic: true,
                    },
                ),
                Bodies.rectangle(
                    width / 2,
                    height + thickness / 2,
                    width * 3,
                    thickness,
                    {
                        isStatic: true,
                    },
                ),
            ];
            World.add(world, boundaries);
        }

        const imagePositions = {
            1: {
                sm: { x: 0, y: 588 },
                md: { x: 0, y: 588 },
                lg: { x: 0, y: 588 },
                xl: { x: 0, y: 588 },
                '2xl': { x: 0, y: 588 },
            },
            2: {
                sm: { x: -188, y: 795 },
                md: { x: -188, y: 795 },
                lg: { x: -230, y: 795 },
                xl: { x: -230, y: 795 },
                '2xl': { x: -230, y: 795 },
            },
            3: {
                sm: { x: 181, y: 734 },
                md: { x: 181, y: 734 },
                lg: { x: 330, y: 734 },
                xl: { x: 330, y: 734 },
                '2xl': { x: 330, y: 734 },
            },
            4: {
                sm: { x: -258, y: 669 },
                md: { x: -258, y: 669 },
                lg: { x: -400, y: 669 },
                xl: { x: -400, y: 669 },
                '2xl': { x: -400, y: 669 },
            },
            5: {
                sm: { x: -356, y: 775 },
                md: { x: -356, y: 775 },
                lg: { x: -530, y: 775 },
                xl: { x: -530, y: 775 },
                '2xl': { x: -530, y: 775 },
            },
            6: {
                sm: { x: -258, y: 669 },
                md: { x: -258, y: 669 },
                lg: { x: -175, y: 670 },
                xl: { x: -175, y: 670 },
                '2xl': { x: -175, y: 670 },
            },
            7: {
                sm: { x: 139, y: 610 },
                md: { x: 139, y: 610 },
                lg: { x: 270, y: 610 },
                xl: { x: 270, y: 610 },
                '2xl': { x: 270, y: 610 },
            },
            8: {
                sm: { x: 33, y: 790 },
                md: { x: 33, y: 790 },
                lg: { x: 24, y: 790 },
                xl: { x: 24, y: 790 },
                '2xl': { x: 24, y: 790 },
            },
            9: {
                sm: { x: 31, y: 730 },
                md: { x: 31, y: 730 },
                lg: { x: 115, y: 730 },
                xl: { x: 115, y: 730 },
                '2xl': { x: 115, y: 730 },
            },
            10: {
                sm: { x: 307, y: 535 },
                md: { x: 307, y: 535 },
                lg: { x: 510, y: 535 },
                xl: { x: 510, y: 535 },
                '2xl': { x: 510, y: 535 },
            },
            11: {
                sm: { x: -175, y: 517 },
                md: { x: -300, y: 517 },
                lg: { x: -300, y: 530 },
                xl: { x: -300, y: 530 },
                '2xl': { x: -300, y: 530 },
            },
            12: {
                sm: { x: 200, y: 786 },
                md: { x: 200, y: 786 },
                lg: { x: 200, y: 786 },
                xl: { x: 200, y: 786 },
                '2xl': { x: 200, y: 786 },
            },
        };

        const newIds = standingFoodImages
            .map((img) => img.id)
            .filter((id) => !bodiesRef.current[id]);

        newIds.forEach((id) => {
            const img = standingFoodImages.find((f) => f.id === id);
            if (!img) return;

            const size = img.responsiveSize?.[viewport] ??
                img.size ?? { width: 150, height: 100 };
            const relPos =
                imagePositions[id as keyof typeof imagePositions]?.[viewport];
            if (!relPos) return;

            const start = { x: centerX + relPos.x, y: relPos.y };

            const category = 0x0001 << id;
            const body = Bodies.rectangle(
                start.x,
                start.y,
                size.width,
                size.height,
                {
                    restitution: 0.1,
                    frictionAir: 0.05,
                    inertia: Infinity,
                    density: 0.05,
                    collisionFilter: { category, mask: category },
                },
            );

            body.label = id.toString();
            Body.setAngle(body, (img.rotation ?? 0) * (Math.PI / 180));
            bodiesRef.current[id] = body;
            World.add(engine.world, body);

            // Optional per-image floor
            const bottomBoundary = Bodies.rectangle(
                centerX,
                start.y + size.height / 2 + 1,
                width,
                2,
                {
                    isStatic: true,
                    collisionFilter: { category, mask: category },
                    label: `bottom-${id}`,
                },
            );
            World.add(engine.world, bottomBoundary);
        });

        if (!runnerRef.current) {
            runnerRef.current = Runner.create();
            Runner.run(runnerRef.current, engine);

            const updateLoop = () => {
                const newPositions: Record<number, { x: number; y: number }> =
                    {};
                const w = window.innerWidth;
                const h = containerRef.current?.offsetHeight ?? 0;

                for (const [idStr, body] of Object.entries(bodiesRef.current)) {
                    const id = parseInt(idStr);
                    const img = standingFoodImages.find((img) => img.id === id);
                    if (!img) continue;

                    const size = img.responsiveSize?.[viewport] ??
                        img.size ?? { width: 150, height: 100 };
                    const halfW = size.width / 2;
                    const halfH = size.height / 2;

                    const relY =
                        imagePositions[id as keyof typeof imagePositions]?.[
                            viewport
                        ]?.y ?? 0;
                    const absY = relY;

                    const clampedX = Math.max(
                        halfW,
                        Math.min(body.position.x, w - halfW),
                    );
                    let newY = body.position.y;

                    if (body.position.y > absY) {
                        Body.setPosition(body, { x: clampedX, y: absY });
                        Body.setVelocity(body, { x: 0, y: 0 });
                        newY = absY;
                    } else {
                        newY = Math.max(
                            halfH,
                            Math.min(body.position.y, h - halfH),
                        );
                    }

                    newPositions[id] = { x: clampedX, y: newY };
                }

                setPositions(newPositions);
                requestAnimationFrame(updateLoop);
            };

            updateLoop();
        }

        return () => {
            Runner.stop(runnerRef.current!);
            runnerRef.current = null;
            World.clear(engine.world, false);
            Engine.clear(engine);
            bodiesRef.current = {};
            setPositions({});
        };
    }, [viewport]);

    const handlePointerDown = (id: number) => (e: React.PointerEvent) => {
        const body = bodiesRef.current[id];
        if (!body) return;

        const img = standingFoodImages.find((img) => img.id === id);
        if (!img) return;

        const size = img.responsiveSize?.[viewport] ??
            img.size ?? { width: 150, height: 100 };
        if (!positions || !positions[id]) return;

        const offsetX = e.clientX - positions[id].x;
        const offsetY = e.clientY - positions[id].y;

        Body.setStatic(body, true);

        let lastX = e.clientX;
        let lastY = e.clientY;
        let lastTime = performance.now();

        let currentX = e.clientX;
        let currentY = e.clientY;
        let currentTime = lastTime;

        const moveHandler = (moveEvent: PointerEvent) => {
            lastX = currentX;
            lastY = currentY;
            lastTime = currentTime;

            currentX = moveEvent.clientX;
            currentY = moveEvent.clientY;
            currentTime = performance.now();

            const halfW = size.width / 2;
            const halfH = size.height / 2;

            const newX = Math.max(
                halfW,
                Math.min(
                    moveEvent.clientX - offsetX,
                    window.innerWidth - halfW,
                ),
            );
            const newY = Math.max(
                halfH,
                Math.min(
                    moveEvent.clientY - offsetY,
                    window.innerHeight - halfH,
                ),
            );

            Body.setPosition(body, { x: newX, y: newY });
        };

        const upHandler = () => {
            const deltaTime = (currentTime - lastTime) / 1000;
            const deltaX = currentX - lastX;
            const deltaY = currentY - lastY;

            const velocity = {
                x: deltaTime > 0 ? deltaX / deltaTime : 0,
                y: deltaTime > 0 ? deltaY / deltaTime : 0,
            };

            velocity.x *= 0.1;
            velocity.y *= 0.1;

            const maxVelocity = 25;
            const cappedVelocity = {
                x: Math.max(-maxVelocity, Math.min(velocity.x, maxVelocity)),
                y: Math.max(-maxVelocity, Math.min(velocity.y, maxVelocity)),
            };

            Body.setVelocity(body, cappedVelocity);
            Body.setStatic(body, false);

            window.removeEventListener('pointermove', moveHandler);
            window.removeEventListener('pointerup', upHandler);
        };

        window.addEventListener('pointermove', moveHandler);
        window.addEventListener('pointerup', upHandler);
    };

    // 7. Brood — merken
    // 10. Lowlander — merken
    // 11. Tromp en Rueb — merken
    // 1. Holies — merken
    // 6. Brandt & Levie — merken
    // 9. Lidl — merken
    // 3. MSC — NGO’s
    // 4. Vrijheidsoep — eigen initiatief
    // 12. Aardappel — merken
    // 8. Fish Tales — merken
    // 5. Bionext — organisaties
    // 2. Aubergine — organisaties

    const imageRenderOrder: number[] = [7, 10, 11, 1, 6, 9, 3, 4, 12, 8, 5, 2];

    return (
        <div
            ref={containerRef}
            className="pointer-events-none absolute z-50 hidden h-[100vh] min-h-[968px] w-full overflow-hidden xl:block"
            style={{ touchAction: 'none' }}
        >
            {imageRenderOrder
                .filter((id) => positions[id] !== undefined)
                .map((id) => {
                    const img = standingFoodImages.find((img) => img.id === id);
                    if (!img) return null;

                    const pos = positions[id]!;
                    const size = img.responsiveSize?.[viewport] ??
                        img.size ?? { width: 150, height: 100 };

                    return (
                        <div
                            key={img.id}
                            className="select-none"
                            style={{
                                position: 'absolute',
                                transform: `translate(${pos.x - size.width / 2}px, ${pos.y - size.height / 2}px)`,
                                width: size.width,
                                height: size.height,
                                pointerEvents: 'auto',
                            }}
                            onPointerDown={handlePointerDown(img.id)}
                        >
                            <div className="pointer-events-auto relative">
                                <Image
                                    src={img.src}
                                    alt={img.alt}
                                    width={1000}
                                    height={1000}
                                    priority
                                    className="pointer-events-auto h-auto w-auto cursor-pointer object-contain"
                                    style={{
                                        width: size.width,
                                        height: size.height,
                                    }}
                                    draggable={false}
                                />
                            </div>
                        </div>
                    );
                })}
        </div>
    );
}
